import { useState, useRef, useCallback } from "react";
import { CloudUpload, Plus } from "lucide-react";
import { Button } from "@/components/ui";
import { DemoFilePicker } from "./DemoFilePicker";
import { useDemoMode } from "../../../lib/use-demo-mode";

const ACCEPTED_TYPES =
  ".pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.txt,.md,.jpg,.jpeg,.png,.gif,.svg,.webp";

interface UploadDropZoneProps {
  collapsed: boolean;
  onFilesSelected: (files: File[]) => void;
  disabled: boolean;
}

export function UploadDropZone({
  collapsed,
  onFilesSelected,
  disabled,
}: UploadDropZoneProps) {
  const isDemoMode = useDemoMode();
  const [isDragOver, setIsDragOver] = useState(false);
  const [demoPickerOpen, setDemoPickerOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleBrowse = () => {
    if (isDemoMode) {
      setDemoPickerOpen(true);
    } else {
      inputRef.current?.click();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (fileList && fileList.length > 0) {
      onFilesSelected(Array.from(fileList));
    }
    // Reset input so the same file can be selected again
    e.target.value = "";
  };

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled && !isDemoMode) setIsDragOver(true);
    },
    [disabled, isDemoMode],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (disabled || isDemoMode) return;

      const droppedFiles = Array.from(e.dataTransfer.files);
      if (droppedFiles.length > 0) {
        onFilesSelected(droppedFiles);
      }
    },
    [disabled, isDemoMode, onFilesSelected],
  );

  const hiddenInput = !isDemoMode && (
    <input
      ref={inputRef}
      type="file"
      multiple
      accept={ACCEPTED_TYPES}
      onChange={handleInputChange}
      className="hidden"
    />
  );

  if (collapsed) {
    return (
      <div>
        {hiddenInput}
        {isDemoMode && (
          <DemoFilePicker
            open={demoPickerOpen}
            onClose={() => setDemoPickerOpen(false)}
            onFilesSelected={(files) => {
              setDemoPickerOpen(false);
              onFilesSelected(files);
            }}
          />
        )}
        <button
          type="button"
          onClick={handleBrowse}
          disabled={disabled}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`w-full flex items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer ${
            disabled
              ? "border-border text-muted cursor-not-allowed"
              : isDragOver
                ? "border-accent bg-accent/10 text-accent ring-2 ring-accent/30"
                : "border-border text-secondary hover:border-accent/50 hover:text-accent"
          }`}
        >
          <Plus size={16} />
          {disabled ? "File limit reached (10)" : "Add more files"}
        </button>
      </div>
    );
  }

  return (
    <>
      {isDemoMode && (
        <DemoFilePicker
          open={demoPickerOpen}
          onClose={() => setDemoPickerOpen(false)}
          onFilesSelected={(files) => {
            setDemoPickerOpen(false);
            onFilesSelected(files);
          }}
        />
      )}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: drop zone needs drag event handlers */}
      <div
        onDragOver={isDemoMode ? undefined : handleDragOver}
        onDragLeave={isDemoMode ? undefined : handleDragLeave}
        onDrop={isDemoMode ? undefined : handleDrop}
        className={`group flex flex-col items-center justify-center rounded-xl bg-page py-12 px-6 text-center transition-all duration-200 ${
          isDragOver && !isDemoMode
            ? "ring-2 ring-accent/50 bg-accent/5"
            : !isDemoMode
              ? "hover:ring-2 hover:ring-accent/30"
              : ""
        }`}
      >
        {hiddenInput}
        <CloudUpload
          size={48}
          className={`mb-4 transition-transform duration-200 ${
            isDragOver && !isDemoMode
              ? "text-accent scale-110"
              : !isDemoMode
                ? "text-muted group-hover:scale-110"
                : "text-muted"
          }`}
        />
        <p className="text-sm font-medium text-primary mb-1">
          Drop files here or browse
        </p>
        <p className="text-xs text-muted mb-4">
          Supports PDF, DOCX, XLSX, PPTX, TXT, and images
        </p>
        <Button variant="primary" onClick={handleBrowse}>
          Browse Files
        </Button>
      </div>
    </>
  );
}
